import React, { useEffect, useState, Children } from 'react';
import { UploadIcon, X, CheckCircle, AlertCircle, FileText, Image as ImageIcon, Zap, Shield, Info, Lock, FileUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { validateFiles, uploadFiles, triggerAsyncAnalysis, updateScanStatus } from '../services/uploadService';
import { createPayment, redirectToPayment } from '../services/paymentService';
import { UploadProgress } from '../types/scan';
import { ForensicTerminalOverlay } from '../components/Upload/ForensicTerminalOverlay';
import { NotificationBanner } from '../components/Notifications/NotificationBanner';
import { ModeSelector, AnalysisMode } from '../components/Upload/ModeSelector';
import { useScans } from '../hooks/useScans';
import { useScanCredits } from '../hooks/useScanCredits';
import { supabase } from '../lib/supabaseClient';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { ScanResultModal } from '../components/Scan/ScanResultModal';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 50,
      damping: 15
    }
  }
};
export function UploadPage() {
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    scans
  } = useScans(user?.id);
  const {
    showToast
  } = useToast();
  const {
    isPro,
    scansCount,
    loading: loadingCredits,
    canScan,
    refetchCredits,
    verifyCanScan,
    refetchError
  } = useScanCredits();
  // Auth guard: redirect unauthenticated users to login (after auth resolves)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth', {
        replace: true
      });
    }
  }, [authLoading, user, navigate]);
  const [conversationName, setConversationName] = useState('');
  const [conversationText, setConversationText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  // PLAN GATE: Screenshots are a paid feature. They're allowed for
  //   - Pro / unlimited users (isPro === true)
  //   - Pay-per-scan users (anyone who has purchased a scan: either has
  //     credits beyond the bundled signup freebie, OR has already used
  //     their free scan and still has scans available)
  // They are BLOCKED for users still on the free-trial credit only.
  const screenshotsAllowed = isPro || scansCount > 1 || scansCount > 0 && scans.length > 0;
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<AnalysisMode>(isPro || scansCount > 0 ? 'relationships' : 'default');
  const [uploading, setUploading] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'pending' | 'processing' | 'completed' | 'failed'>('idle');
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  // New state for scan results
  const [scanResult, setScanResult] = useState<{
    score?: number;
    riskLevel?: string;
    errorMessage?: string;
  }>({});
  const [currentScanId, setCurrentScanId] = useState<string | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  // CRITICAL: Kill switch - force exit processing state when analysisStatus changes
  // This ensures UI never stays stuck in "Processing" after a response
  useEffect(() => {
    if (analysisStatus === 'completed' || analysisStatus === 'failed') {
      console.log('[KILL SWITCH] Forcing uploading=false, status:', analysisStatus);
      setUploading(false);
    }
  }, [analysisStatus]);
  // Track page view on mount
  useEffect(() => {
    if (!loadingCredits && user?.id) {
      trackCriticalEvent('Upload_Page_Viewed', {
        is_pro: isPro,
        scans_remaining: scansCount,
        has_previous_scans: scans.length > 0
      });
    }
  }, [loadingCredits, user?.id, isPro, scansCount, scans.length]);
  useEffect(() => {
    const premiumModes: AnalysisMode[] = ['relationships', 'ex', 'friends', 'family', 'workplace'];
    if (!isPro && scansCount === 0 && premiumModes.includes(selectedMode)) {
      setSelectedMode('default');
    }
  }, [isPro, scansCount, selectedMode]);
  // Track mode selection
  useEffect(() => {
    if (selectedMode && !loadingCredits && user?.id) {
      const premiumModes: AnalysisMode[] = ['relationships', 'ex', 'friends', 'family', 'workplace'];
      trackCriticalEvent('Analysis_Mode_Selected', {
        mode: selectedMode,
        is_premium_mode: premiumModes.includes(selectedMode),
        user_plan: isPro ? 'pro' : scansCount > 0 ? 'pay-per-scan' : 'free'
      });
    }
  }, [selectedMode]);
  const handlePayment = async (plan: 'single' | 'pro') => {
    if (!user?.email || !user?.id) {
      setError('You must be logged in to purchase');
      return;
    }
    // Track payment initiation
    await trackCriticalEvent('Payment_Initiated', {
      plan_type: plan,
      price: plan === 'single' ? 2.99 : 9.99,
      trigger_location: 'upload_paywall',
      is_upgrade: false,
      current_plan: isPro ? 'pro' : 'free'
    });
    setPaymentLoading(plan === 'single' ? 'Basic' : 'Unlimited');
    setError(null);
    try {
      const response = await createPayment(plan);
      // Track redirect success
      await trackCriticalEvent('Payment_Redirect_Success', {
        plan_type: plan,
        authorization_url: response.authorization_url
      });
      redirectToPayment(response.authorization_url);
    } catch (err: any) {
      console.error('Payment error:', err);
      showToast(err.message || 'Payment server busy, try again.', 'error');
      setError(err.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setPaymentLoading(null);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Defensive: even if the UI is gated, refuse files from free-tier users.
    if (!screenshotsAllowed) {
      setError('Screenshot uploads are a paid feature. Upgrade or purchase a scan to unlock.');
      e.target.value = '';
      return;
    }
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const validationErrors = validateFiles(files);
      if (validationErrors.length > 0) {
        setError(validationErrors[0].message);
        return;
      }
      setSelectedFiles(files);
      setError(null);
    }
  };
  // If the user's plan status changes to free-tier (e.g. after using a credit),
  // drop any pending screenshots so we never submit them on a downgrade.
  useEffect(() => {
    if (!screenshotsAllowed && selectedFiles.length > 0) {
      setSelectedFiles([]);
    }
  }, [screenshotsAllowed, selectedFiles.length]);
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setScanResult({});
    // CRITICAL: Disable button immediately to prevent double-tap
    // The DB has an atomic lock, but we also guard on the frontend
    if (uploading) return;
    setUploading(true);
    if (!user) {
      setError('You must be logged in to upload files');
      setUploading(false);
      return;
    }
    // CRITICAL: Verify with backend before allowing scan (not just local state)
    console.log('[SUBMIT] Verifying scan permission with backend...');
    const verification = await verifyCanScan();
    if (!verification.allowed) {
      console.log('[SUBMIT] Backend blocked scan:', verification.reason);
      if (verification.reason === 'NO_CREDITS') {
        // Track paywall display
        await trackCriticalEvent('Paywall_Displayed', {
          trigger_action: 'upload_attempt',
          is_pro: isPro,
          scans_remaining: 0,
          reason: 'backend_verification_no_credits'
        });
        setShowPaywallModal(true);
        setUploading(false);
        return;
      }
      if (verification.reason === 'VERIFICATION_FAILED') {
        setError('Unable to verify scan credits. Please try again.');
        showToast('Credit verification failed. Please refresh and try again.', 'error');
        setUploading(false);
        return;
      }
      if (verification.reason === 'NOT_LOGGED_IN') {
        setError('You must be logged in to upload files');
        setUploading(false);
        return;
      }
      // Generic fallback
      setError('Unable to start scan. Please try again.');
      setUploading(false);
      return;
    }
    console.log('[SUBMIT] Backend verified scan is allowed');
    if (!conversationName.trim()) {
      setError('Please enter a conversation name');
      setUploading(false);
      return;
    }
    if (!conversationText.trim() && selectedFiles.length === 0) {
      setError('Please paste conversation text or upload at least one screenshot');
      setUploading(false);
      return;
    }
    if (selectedFiles.length > 0) {
      const validationErrors = validateFiles(selectedFiles);
      if (validationErrors.length > 0) {
        setError(validationErrors[0].message);
        setUploading(false);
        return;
      }
    }
    // Track scan initiation
    await trackCriticalEvent('Scan_Initiated', {
      conversation_name: conversationName,
      has_text_input: conversationText.trim().length > 0,
      has_file_uploads: selectedFiles.length > 0,
      file_count: selectedFiles.length,
      file_types: selectedFiles.map(f => f.type),
      analysis_mode: selectedMode,
      is_pro: isPro,
      scans_remaining_before: scansCount
    });
    setAnalysisStatus('pending');
    let scanId: string | null = null;
    let realtimeChannel: any = null;
    let timeoutId: NodeJS.Timeout | null = null;
    // Helper function to cleanup and exit processing state
    const exitProcessingState = async (status: 'completed' | 'failed' | 'idle') => {
      console.log('[EXIT PROCESSING] Setting status to:', status);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (realtimeChannel) {
        supabase.removeChannel(realtimeChannel);
        realtimeChannel = null;
      }
      setAnalysisStatus(status);
      setUploading(false);
      // CRITICAL: Always re-fetch credits from backend after any scan event
      console.log('[EXIT PROCESSING] Re-fetching credits from backend...');
      await refetchCredits();
      console.log('[EXIT PROCESSING] Credits re-fetched');
    };
    try {
      // Upload files first if any
      let fileUrls: string[] = [];
      if (selectedFiles.length > 0) {
        const progress: UploadProgress[] = selectedFiles.map(file => ({
          fileName: file.name,
          progress: 0,
          status: 'pending'
        }));
        setUploadProgress(progress);
        fileUrls = await uploadFiles(selectedFiles, user.id, (fileName, prog) => {
          setUploadProgress(prev => prev.map(p => p.fileName === fileName ? {
            ...p,
            progress: prog,
            status: 'completed'
          } : p));
        });
        // Track upload completion
        const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024);
        await trackCriticalEvent('Scan_Upload_Completed', {
          scan_id: scanId || 'pending',
          file_count: selectedFiles.length,
          total_file_size_mb: Math.round(totalSize * 100) / 100,
          upload_duration_seconds: 0
        });
      }
      const conversationTextValue = conversationText.trim() || '';
      console.log('[SCAN INSERT] Preparing scan data:', {
        user_id: user.id,
        title: conversationName,
        mode: selectedMode,
        has_files: fileUrls.length > 0,
        has_text: conversationTextValue.length > 0,
        text_length: conversationTextValue.length
      });
      // Insert scan record
      const {
        data: scanData,
        error: insertError
      } = await supabase.from('scans').insert({
        user_id: user.id,
        title: conversationName,
        file_urls: fileUrls,
        conversation_text: conversationTextValue,
        mode: selectedMode,
        status: 'pending',
        created_at: new Date().toISOString(),
        is_unlocked: isPro || scansCount > 0
      }).select().single();
      if (insertError) {
        console.error('[SCAN INSERT] Error:', insertError);
        throw insertError;
      }
      scanId = scanData.id;
      setCurrentScanId(scanId);
      console.log('[SCAN INSERT] Success - Scan ID:', scanId);
      // Set up timeout - 30 seconds for initial response
      timeoutId = setTimeout(() => {
        console.log('[TIMEOUT] No status update after 30s - scan processing in background');
        setAnalysisStatus('processing');
        showToast("Analysis is taking longer than expected. You'll be notified when complete.", 'info');
      }, 30000);
      // Set up realtime subscription
      realtimeChannel = supabase.channel(`scan-${scanId}`).on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'scans',
        filter: `id=eq.${scanId}`
      }, async payload => {
        const updatedScan = payload.new as any;
        console.log('[REALTIME] Scan update received:', {
          status: updatedScan.status,
          score: updatedScan.score,
          risk_level: updatedScan.risk_level,
          has_analysis: !!updatedScan.analysis
        });
        // Clear timeout when we receive any status update
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (updatedScan.status === 'processing') {
          setAnalysisStatus('processing');
        }
        if (updatedScan.status === 'completed') {
          // CRITICAL: Distinguish between a real score of 0 and a missing/null score.
          // A null score means the backend never computed one (OCR failed, empty input, etc.)
          const hasRealScore = typeof updatedScan.score === 'number' && !Number.isNaN(updatedScan.score);
          const hasRealRisk = typeof updatedScan.risk_level === 'string' && updatedScan.risk_level.length > 0;
          if (!hasRealScore && !hasRealRisk) {
            console.warn('[REALTIME] Scan marked completed but score & risk_level are null - backend extracted no content.');
            setScanResult({
              errorMessage: "We couldn't extract a conversation from your upload. Please try with clearer screenshots or paste the conversation text directly."
            });
            setShowResultModal(true);
            await exitProcessingState('failed');
            return;
          }
          setScanResult({
            score: hasRealScore ? updatedScan.score : undefined,
            riskLevel: hasRealRisk ? updatedScan.risk_level : undefined
          });
          setShowResultModal(true);
          setShowNotification(true);
          // Track analysis completion
          trackCriticalEvent('Scan_Analysis_Completed', {
            scan_id: scanId,
            toxicity_score: updatedScan.score || 0,
            risk_level: updatedScan.risk_level || 'unknown',
            patterns_detected: Array.isArray(updatedScan.patterns) ? updatedScan.patterns.length : 0,
            message_count: updatedScan.message_count || 0,
            analysis_duration_seconds: 0
          });
          // CRITICAL: Exit processing state and re-fetch credits
          await exitProcessingState('completed');
        }
        if (updatedScan.status === 'failed') {
          const errorMsg = 'Analysis failed. Please try again.';
          setScanResult({
            errorMessage: errorMsg
          });
          setError(errorMsg);
          setShowResultModal(true);
          // Track analysis failure
          trackCriticalEvent('Scan_Analysis_Failed', {
            scan_id: scanId,
            error_type: 'analysis_failed',
            retry_attempted: false
          });
          // CRITICAL: Exit processing state and re-fetch credits
          await exitProcessingState('failed');
        }
      }).subscribe();
      // Trigger async analysis
      console.log('[ANALYSIS] Triggering async analysis...');
      try {
        const analysisResponse = await triggerAsyncAnalysis(scanId, fileUrls, user.id, conversationTextValue || undefined, selectedMode);
        // CRITICAL: Check if backend returned "not allowed" response
        if (analysisResponse && analysisResponse.allowed === false) {
          console.log('[ANALYSIS] Backend blocked scan:', analysisResponse.reason);
          if (analysisResponse.reason === 'NO_CREDITS') {
            // Show paywall modal
            await trackCriticalEvent('Paywall_Displayed', {
              trigger_action: 'scan_response',
              is_pro: isPro,
              scans_remaining: 0,
              reason: 'backend_response_no_credits'
            });
            setScanResult({
              errorMessage: 'No scans remaining. Please purchase a subscription to continue.'
            });
            setShowPaywallModal(true);
            setShowResultModal(true);
            await exitProcessingState('failed');
            return;
          }
          // Generic not allowed
          setScanResult({
            errorMessage: analysisResponse.reason || 'Scan not allowed'
          });
          setShowResultModal(true);
          await exitProcessingState('failed');
          return;
        }
        console.log('[ANALYSIS] Trigger successful');
        // Track analysis start
        await trackCriticalEvent('Scan_Analysis_Started', {
          scan_id: scanId,
          analysis_mode: selectedMode,
          has_files: fileUrls.length > 0,
          has_text: conversationTextValue.length > 0
        });
      } catch (analysisError: any) {
        console.error('[ANALYSIS] Edge Function error:', analysisError);
        // Clear timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        // Check if it's a server overload error
        if (analysisError.message === 'SERVER_OVERLOAD') {
          console.log('[ANALYSIS] Server overload - scan will process automatically');
          showToast('Server is experiencing high load. Your scan is queued and will process automatically. Check back in a few minutes.', 'info');
          setAnalysisStatus('processing');
          // CRITICAL: Re-fetch credits even on server overload
          await refetchCredits();
          setTimeout(() => {
            setUploading(false);
            navigate('/dashboard');
          }, 3000);
          return;
        }
        // For other errors, mark as failed
        if (scanId) {
          await updateScanStatus(scanId, 'failed');
        }
        // Track failure
        await trackCriticalEvent('Scan_Analysis_Failed', {
          scan_id: scanId,
          error_type: analysisError.message || 'unknown_error',
          retry_attempted: false
        });
        let errorMessage = 'Failed to start analysis. Please try again.';
        if (analysisError.message?.includes('520') || analysisError.message?.includes('502')) {
          errorMessage = 'Server temporarily unavailable. Your scan is saved and will process automatically.';
        } else if (analysisError.message) {
          errorMessage = analysisError.message;
        }
        setScanResult({
          errorMessage: errorMessage
        });
        setError(errorMessage);
        setShowResultModal(true);
        // CRITICAL: Exit processing state
        await exitProcessingState('failed');
      }
    } catch (err: any) {
      console.error('[SUBMIT] Error in handleSubmit:', err);
      let errorMessage = 'Failed to analyze conversation. Please try again.';
      if (err.message?.includes('520') || err.message?.includes('502')) {
        errorMessage = 'Server temporarily unavailable. Please try again in a few moments.';
      } else if (err.code === 'PGRST116') {
        errorMessage = 'Database error. Please check your connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setScanResult({
        errorMessage: errorMessage
      });
      setUploadProgress([]);
      setShowResultModal(true);
      // CRITICAL: Exit processing state
      await exitProcessingState('failed');
    }
  };
  useEffect(() => {
    return () => {
      const channels = supabase.getChannels();
      channels.forEach(channel => {
        if (channel.topic.startsWith('scan-')) {
          supabase.removeChannel(channel);
        }
      });
    };
  }, []);
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-10494">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-10495" />

      <WebsiteHeader data-id="element-10496" />

      {/* Processing Overlay */}
      {uploading && <ForensicTerminalOverlay status={analysisStatus} mode={selectedMode} onTimeout={() => {
      console.log('[OVERLAY] Timeout triggered');
    }} data-id="element-10497" />}

      {/* Result Modal */}
      <ScanResultModal isOpen={showResultModal} status={analysisStatus === 'completed' ? 'completed' : 'failed'} score={scanResult.score} riskLevel={scanResult.riskLevel} errorMessage={scanResult.errorMessage} onViewResults={() => navigate(`/analysis?id=${currentScanId}`)} onRetry={() => {
      setShowResultModal(false);
      setUploading(false);
      setAnalysisStatus('idle');
    }} onClose={() => {
      setShowResultModal(false);
      navigate('/dashboard');
    }} data-id="element-10498" />

      {showNotification && <NotificationBanner message="Analysis Complete. Report Generated." onClose={() => setShowNotification(false)} data-id="element-10499" />}

      {/* Paywall Modal */}
      <AnimatePresence data-id="element-10500">
        {showPaywallModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowPaywallModal(false)} data-id="element-10501">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-[#0a0a0a] border-2 border-[#FF0000] max-w-md w-full p-8 relative" data-id="element-10502">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#FF0000]" data-id="element-10503" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#FF0000]" data-id="element-10504" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#FF0000]" data-id="element-10505" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#FF0000]" data-id="element-10506" />

              <button onClick={() => setShowPaywallModal(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors" data-id="element-10507">
                <X size={20} data-id="element-10508" />
              </button>

              <div className="text-center mb-6" data-id="element-10509">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF0000]/10 border border-[#FF0000] mb-4" data-id="element-10510">
                  <Lock className="w-8 h-8 text-[#FF0000]" data-id="element-10511" />
                </div>
                <h3 className="text-2xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-10512">
                  No Scans Remaining
                </h3>
                <p className="text-gray-400 font-mono text-sm" data-id="element-10513">
                  You've used your free scan. Purchase a single scan for $2.99
                  or subscribe for unlimited access at $9.99/month.
                </p>
              </div>

              {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-mono" data-id="element-10514">
                  {error}
                </div>}

              <div className="space-y-3" data-id="element-10515">
                <button onClick={() => handlePayment('single')} disabled={paymentLoading !== null} className="w-full bg-transparent border border-[#FF0000]/50 text-[#FF0000] py-3 font-bold hover:bg-[#FF0000]/10 transition-all text-sm uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-10516">
                  {paymentLoading === 'Basic' ? <>
                      <div className="w-4 h-4 border-2 border-[#FF0000]/30 border-t-[#FF0000] rounded-full animate-spin" data-id="element-10517" />
                      <span data-id="element-10518">Redirecting...</span>
                    </> : <>
                      <Zap size={16} data-id="element-10519" />
                      <span data-id="element-10520">Pay per Scan ($2.99)</span>
                    </>}
                </button>
                <button onClick={() => handlePayment('pro')} disabled={paymentLoading !== null} className="w-full bg-white text-black py-3 font-bold hover:bg-gray-200 transition-all text-sm uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-10521">
                  {paymentLoading === 'Unlimited' ? <>
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-10522" />
                      <span data-id="element-10523">Redirecting...</span>
                    </> : <>
                      <Shield size={16} data-id="element-10524" />
                      <span data-id="element-10525">Get Pro ($9.99/mo)</span>
                    </>}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-lg mx-auto w-full relative z-10" data-id="element-10526">
        <motion.main className="px-6 pt-12 pb-24" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10527">
          {/* Page Header */}
          <motion.div variants={itemVariants} className="text-center mb-10" data-id="element-10528">
            <div className="inline-flex items-center gap-2 bg-[#FF0000]/10 text-[#FF0000] px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6 border border-[#FF0000]/30 shadow-[0_0_15px_rgba(255,0,0,0.2)]" data-id="element-10529">
              <FileUp size={14} data-id="element-10530" />
              <span data-id="element-10531">Evidence Upload</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-10532">
              Upload Chat Data
            </h1>
            <p className="text-gray-400 leading-relaxed text-sm max-w-sm mx-auto font-mono" data-id="element-10533">
              Paste conversation text or upload screenshots to identify toxicity
              patterns and hidden manipulation.
            </p>
          </motion.div>

          {/* Scans Remaining Display */}
          {!loadingCredits && <motion.div variants={itemVariants} className="bg-[#0a0a0a] p-6 mb-8 border border-gray-800 relative overflow-hidden group" data-id="element-10534">
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-10535" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-10536" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-10537" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-10538" />

              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF0000]/5 rounded-full blur-3xl -mr-16 -mt-16 transition-opacity opacity-50 group-hover:opacity-100" data-id="element-10539"></div>

              <div className="flex items-start gap-4 mb-5 relative z-10" data-id="element-10540">
                <div className="flex-shrink-0 w-12 h-12 bg-gray-900 flex items-center justify-center border border-gray-800" data-id="element-10541">
                  <Shield size={24} className="text-gray-500" data-id="element-10542" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-10543">
                  <h3 className="font-bold text-white mb-1 uppercase tracking-wide" data-id="element-10544">
                    {isPro ? 'Pro Plan Active' : 'Scans Available'}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed font-mono" data-id="element-10545">
                    {isPro ? 'Unlimited scans enabled' : scansCount > 0 ? `${scansCount} ${scansCount === 1 ? 'scan' : 'scans'} remaining` : 'No scans remaining. Please purchase a subscription to continue.'}
                  </p>
                  {refetchError && <p className="text-xs text-red-400 mt-1 font-mono" data-id="element-10546">
                      {refetchError} - Scans blocked until credits verified
                    </p>}
                </div>
              </div>

              {!isPro && <div className="flex gap-3 relative z-10" data-id="element-10547">
                  <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => handlePayment('single')} disabled={paymentLoading !== null} className="flex-1 bg-transparent border border-[#FF0000]/50 text-[#FF0000] py-3 font-bold hover:bg-[#FF0000]/10 transition-all text-xs uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-10548">
                    {paymentLoading === 'Basic' ? <>
                        <div className="w-4 h-4 border-2 border-[#FF0000]/30 border-t-[#FF0000] rounded-full animate-spin" data-id="element-10549" />
                        <span data-id="element-10550">Redirecting...</span>
                      </> : <>
                        <Zap size={16} data-id="element-10551" />
                        <span data-id="element-10552">Pay per Scan ($2.99)</span>
                      </>}
                  </motion.button>
                  <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => handlePayment('pro')} disabled={paymentLoading !== null} className="flex-1 bg-white text-black py-3 font-bold hover:bg-gray-200 transition-all text-xs uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-10553">
                    {paymentLoading === 'Unlimited' ? <>
                        <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-10554" />
                        <span data-id="element-10555">Redirecting...</span>
                      </> : <>
                        <Shield size={16} data-id="element-10556" />
                        <span data-id="element-10557">Get Pro ($9.99/mo)</span>
                      </>}
                  </motion.button>
                </div>}
            </motion.div>}

          {/* Error Message */}
          <AnimatePresence data-id="element-10558">
            {error && <motion.div initial={{
            opacity: 0,
            height: 0,
            marginBottom: 0
          }} animate={{
            opacity: 1,
            height: 'auto',
            marginBottom: 24
          }} exit={{
            opacity: 0,
            height: 0,
            marginBottom: 0
          }} className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 flex items-start gap-3 overflow-hidden" data-id="element-10559">
                <AlertCircle className="text-[#FF0000] flex-shrink-0 mt-0.5" size={20} data-id="element-10560" />
                <p className="text-[#FF0000] text-sm font-mono" data-id="element-10561">{error}</p>
              </motion.div>}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-8" data-id="element-10562">
            {/* Mode Selector */}
            <motion.div variants={itemVariants} data-id="element-10563">
              <ModeSelector selectedMode={selectedMode} onModeChange={setSelectedMode} disabled={uploading} isPro={isPro} hasCredits={canScan} onUpgradeTrigger={() => setShowPaywallModal(true)} data-id="element-10564" />
            </motion.div>

            {/* Conversation Name */}
            <motion.div variants={itemVariants} className="bg-[#0a0a0a] p-6 border border-gray-800 relative" data-id="element-10565">
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-10566" />
              <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-10567" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-10568" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-10569" />

              <label className="block text-xs font-bold text-gray-500 mb-3 uppercase tracking-widest font-mono" data-id="element-10570">
                Case Title / Subject
              </label>
              <input type="text" placeholder="e.g., Chat with Alex" value={conversationName} onChange={e => setConversationName(e.target.value)} disabled={uploading} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white placeholder-gray-600 transition-all text-sm font-mono" data-id="element-10571" />
            </motion.div>

            {/* Upload Method Tabs */}
            <motion.div variants={itemVariants} className="bg-[#0a0a0a] border border-gray-800 relative" data-id="element-10572">
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-10573" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-10574" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-10575" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-10576" />

              {/* Text Input Section */}
              <div className="p-6 border-b border-gray-800" data-id="element-10577">
                <div className="flex items-center gap-2 mb-3" data-id="element-10578">
                  <div className="p-1.5 bg-gray-900 border border-gray-800" data-id="element-10579">
                    <FileText size={14} className="text-gray-400" data-id="element-10580" />
                  </div>
                  <label className="text-sm font-bold text-white uppercase tracking-wide" data-id="element-10581">
                    Paste Text Data
                  </label>
                  <span className="text-[10px] text-gray-500 font-mono bg-gray-900 px-2 py-0.5 border border-gray-800 uppercase" data-id="element-10582">
                    Optional
                  </span>
                </div>
                <div className="relative" data-id="element-10583">
                  {uploading && <div className="scanning-line" data-id="element-10584"></div>}
                  <textarea placeholder="Paste raw conversation logs here..." value={conversationText} onChange={e => setConversationText(e.target.value)} disabled={uploading} rows={6} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white placeholder-gray-600 resize-none transition-all text-sm font-mono leading-relaxed" data-id="element-10585" />
                </div>
              </div>

              {/* File Upload Section */}
              <div className="p-6" data-id="element-10586">
                <div className="flex items-center gap-2 mb-3" data-id="element-10587">
                  <div className="p-1.5 bg-gray-900 border border-gray-800" data-id="element-10588">
                    <ImageIcon size={14} className="text-gray-400" data-id="element-10589" />
                  </div>
                  <label className="text-sm font-bold text-white uppercase tracking-wide" data-id="element-10590">
                    Upload Evidence
                  </label>
                  <span className="text-[10px] text-gray-500 font-mono bg-gray-900 px-2 py-0.5 border border-gray-800 uppercase" data-id="element-10591">
                    Optional
                  </span>
                </div>

                <motion.div whileHover={!uploading ? {
                backgroundColor: 'rgba(255, 0, 0, 0.05)',
                borderColor: '#FF0000'
              } : {}} className="border-2 border-dashed border-gray-800 p-8 text-center transition-all bg-[#050505] group" data-id="element-10592">
                  <input type="file" id="upload-files" className="hidden" onChange={handleFileChange} accept="image/*,.pdf" multiple disabled={uploading} data-id="element-10593" />
                  <label htmlFor="upload-files" className={`cursor-pointer block ${uploading ? 'cursor-not-allowed opacity-50' : ''}`} data-id="element-10594">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-[#0a0a0a] mb-4 border border-gray-800 group-hover:border-[#FF0000] transition-colors" data-id="element-10595">
                      <UploadIcon className="w-6 h-6 text-gray-400 group-hover:text-[#FF0000] transition-colors" data-id="element-10596" />
                    </div>
                    <p className="font-bold text-white mb-1 uppercase tracking-wide text-sm" data-id="element-10597">
                      Drop Screenshots Here
                    </p>
                    <p className="text-[10px] text-gray-500 font-mono uppercase" data-id="element-10598">
                      Max 10 files • 5MB each • JPG/PNG/PDF
                    </p>
                  </label>
                </motion.div>

                {/* Selected Files */}
                <AnimatePresence data-id="element-10599">
                  {selectedFiles.length > 0 && !uploading && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="mt-4 space-y-2" data-id="element-10600">
                      {selectedFiles.map((file, index) => <motion.div key={`${file.name}-${index}`} initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: 10
                  }} className="flex items-center justify-between bg-[#050505] p-3 border border-gray-800 group" data-id="element-10601">
                          <div className="flex items-center gap-3 overflow-hidden" data-id="element-10602">
                            <div className="w-8 h-8 bg-gray-900 flex items-center justify-center border border-gray-800 flex-shrink-0" data-id="element-10603">
                              <ImageIcon size={14} className="text-gray-500" data-id="element-10604" />
                            </div>
                            <span className="text-sm text-gray-300 truncate font-mono" data-id="element-10605">
                              {file.name}
                            </span>
                          </div>
                          <button type="button" onClick={() => removeFile(index)} className="p-1.5 hover:bg-[#FF0000]/10 hover:text-[#FF0000] text-gray-500 transition-colors" aria-label="Remove file" data-id="element-10606">
                            <X size={14} data-id="element-10607" />
                          </button>
                        </motion.div>)}
                    </motion.div>}
                </AnimatePresence>

                {/* Upload Progress */}
                {uploading && uploadProgress.length > 0 && <div className="mt-4 space-y-2" data-id="element-10608">
                    {uploadProgress.map((progress, index) => <div key={index} className="flex items-center bg-[#050505] p-3 border border-gray-800" data-id="element-10609">
                        <span className="text-sm text-gray-300 truncate flex-1 font-mono" data-id="element-10610">
                          {progress.fileName}
                        </span>
                        {progress.status === 'completed' ? <CheckCircle size={16} className="text-green-500 ml-3" data-id="element-10611" /> : <div className="w-4 h-4 border-2 border-gray-800 border-t-[#FF0000] rounded-full animate-spin ml-3" data-id="element-10612"></div>}
                      </div>)}
                  </div>}
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.button variants={itemVariants} whileHover={!uploading && canScan && !refetchError && (conversationText.trim() || selectedFiles.length > 0) ? {
            scale: 1.02,
            boxShadow: '0 0 20px rgba(255, 0, 0, 0.4)'
          } : {}} whileTap={!uploading && canScan && !refetchError ? {
            scale: 0.98
          } : {}} type={refetchError ? 'button' : 'submit'} onClick={refetchError ? e => {
            e.preventDefault();
            refetchCredits();
          } : undefined} disabled={uploading || !refetchError && (!conversationText.trim() && selectedFiles.length === 0 || loadingCredits || !canScan)} className="w-full bg-[#FF0000] text-white py-4 font-bold hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(255,0,0,0.2)] flex items-center justify-center gap-2 uppercase tracking-widest text-sm" data-id="element-10613">
              {uploading ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10614"></div>
                  <span data-id="element-10615">Running Analysis...</span>
                </> : loadingCredits ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10616"></div>
                  <span data-id="element-10617">Verifying credits...</span>
                </> : refetchError ? <>
                  <AlertCircle size={18} data-id="element-10618" />
                  <span data-id="element-10619">Couldn't load credits — Tap to retry</span>
                </> : !canScan ? <>
                  <Lock size={18} data-id="element-10620" />
                  <span data-id="element-10621">No scans remaining. Purchase to continue.</span>
                </> : <>
                  <Zap size={18} className="fill-white" data-id="element-10622" />
                  <span data-id="element-10623">
                    {isPro ? 'Initiate Scan (Unlimited)' : `Initiate Scan (${scansCount} remaining)`}
                  </span>
                </>}
            </motion.button>
          </form>

          {/* Tips Section */}
          <motion.div variants={itemVariants} className="bg-[#0a0a0a] border border-gray-800 p-6 mt-10 relative" data-id="element-10624">
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-10625" />
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-10626" />
            <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-10627" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-10628" />

            <h3 className="font-bold text-white mb-4 flex items-center gap-2 text-xs uppercase tracking-widest font-mono" data-id="element-10629">
              <Info size={14} className="text-[#FF0000]" data-id="element-10630" />
              <span data-id="element-10631">Protocol Guidelines</span>
            </h3>
            <ul className="space-y-3 text-xs text-gray-400 font-mono" data-id="element-10632">
              <li className="flex items-start gap-3" data-id="element-10633">
                <div className="w-1 h-1 bg-[#FF0000] mt-1.5 flex-shrink-0" data-id="element-10634"></div>
                <span data-id="element-10635">
                  Include multiple messages for accurate pattern triangulation
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-10636">
                <div className="w-1 h-1 bg-[#FF0000] mt-1.5 flex-shrink-0" data-id="element-10637"></div>
                <span data-id="element-10638">Raw text logs and screenshots are equally effective</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-10639">
                <div className="w-1 h-1 bg-[#FF0000] mt-1.5 flex-shrink-0" data-id="element-10640"></div>
                <span data-id="element-10641">
                  <Lock size={10} className="inline mr-1" data-id="element-10642" />
                  Data is encrypted end-to-end. Zero-knowledge architecture.
                </span>
              </li>
            </ul>
          </motion.div>
        </motion.main>
      </div>
    </div>;
}